import { useCallback, useEffect, useMemo, useRef, useState } from "react";

/**
 * Tracks whether a tab panel scroll container has overflow so callers can add
 * bottom padding only when scrolling is actually possible (avoids phantom scroll).
 *
 * @returns {{
 *   panelsRef: import('react').RefObject<HTMLElement|null>,
 *   hasOverflow: boolean,
 *   handlePanelsLayout: Function,
 *   panelStyleOverrides: { boxSizing: string, paddingBottom: number },
 * }}
 */
export function useTabPanelsOverflow() {
  const panelsRef = useRef(null);
  const [hasOverflow, setHasOverflow] = useState(false);

  const updatePanelsOverflow = useCallback(() => {
    const el = panelsRef.current;
    if (!el) return;

    const style = window.getComputedStyle(el);
    const paddingBottom = parseFloat(style.paddingBottom || "0") || 0;
    const contentScrollHeight = el.scrollHeight - paddingBottom;

    setHasOverflow(contentScrollHeight > el.clientHeight + 1);
  }, []);

  const handlePanelsLayout = useCallback(() => {
    updatePanelsOverflow();
  }, [updatePanelsOverflow]);

  useEffect(() => {
    const el = panelsRef.current;
    if (!el) return;

    updatePanelsOverflow();

    let raf = null;
    const schedule = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        raf = null;
        updatePanelsOverflow();
      });
    };

    const ro =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(schedule)
        : null;
    ro?.observe(el);

    const mo =
      typeof MutationObserver !== "undefined"
        ? new MutationObserver(schedule)
        : null;
    mo?.observe(el, { childList: true, subtree: true, attributes: true });

    return () => {
      if (raf) cancelAnimationFrame(raf);
      ro?.disconnect?.();
      mo?.disconnect?.();
    };
  }, [updatePanelsOverflow]);

  const panelStyleOverrides = useMemo(
    () => ({
      boxSizing: "border-box",
      paddingBottom: hasOverflow ? 24 : 0,
    }),
    [hasOverflow],
  );

  return {
    panelsRef,
    hasOverflow,
    handlePanelsLayout,
    panelStyleOverrides,
  };
}
